import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { SavesProvider } from "@/context/SavesContext";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/Nav";
import Home from "@/pages/Home";
const Listing = lazy(() => import("@/pages/Listing"));
const PublicPin = lazy(() => import("@/pages/PublicPin"));
const PublicPlaces = lazy(() => import("@/pages/PublicPlaces"));
const PublicSearch = lazy(() => import("@/pages/PublicSearch"));
import Auth from "@/pages/Auth";
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Cart = lazy(() => import("@/pages/Cart"));
const CheckoutSuccess = lazy(() => import("@/pages/Checkout").then((m) => ({ default: m.CheckoutSuccess })));
const CheckoutCancel = lazy(() => import("@/pages/Checkout").then((m) => ({ default: m.CheckoutCancel })));
const BookingSuccess = lazy(() => import("@/pages/Booking").then((m) => ({ default: m.BookingSuccess })));
const BookingCancel = lazy(() => import("@/pages/Booking").then((m) => ({ default: m.BookingCancel })));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const DashboardPhotos = lazy(() => import("@/pages/DashboardPhotos"));
const Photographer = lazy(() => import("@/pages/Photographer"));
const Feed = lazy(() => import("@/pages/Feed"));
const Saved = lazy(() => import("@/pages/Saved"));
const Admin = lazy(() => import("@/pages/Admin"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const CommunityGuidelines = lazy(() => import("@/pages/CommunityGuidelines"));
const Help = lazy(() => import("@/pages/Help"));
const Join = lazy(() => import("@/pages/Join"));
const GigScanner = lazy(() => import("@/pages/GigScanner"));
const ClaimListing = lazy(() => import("@/pages/ClaimListing"));
const PinPage = lazy(() => import("@/pages/PinPage"));
import Welcome from "@/pages/Welcome";
const Pitch = lazy(() => import("@/pages/Pitch"));
import RequireAuth from "@/components/RequireAuth";
import CookieBanner from "@/components/CookieBanner";
import Footer from "@/components/Footer";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import InstallPrompt from "@/components/InstallPrompt";
import ScrollToTop from "@/components/ScrollToTop";
import DevSwitcher from "@/components/DevSwitcher";
import SnapBackPill from "@/components/SnapBackPill";
import { LegalNotice } from "@/components/LegalNotice";

const PageLoader = () => (
    <div className="flex items-center justify-center py-32" data-testid="page-loader">
        <div className="w-7 h-7 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
    </div>
);

// Anything wrapped in <Gated> requires a signed-in account.
const Gated = (el) => <RequireAuth>{el}</RequireAuth>;

function AppRoutes() {
    const location = useLocation();
    // Detect OAuth callback via URL fragment synchronously during render
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <div className="min-h-screen flex flex-col">
            <ScrollToTop />
            <ImpersonationBanner />
            <Nav />
            <LegalNotice />
            <main className="flex-1">
                <Suspense fallback={<PageLoader />}>
                <Routes>
                    {/* Public — no account required */}
                    <Route path="/welcome" element={<Welcome />} />
                    <Route path="/pitch" element={<Pitch />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/auth/forgot" element={<ForgotPassword />} />
                    <Route path="/auth/reset" element={<ResetPassword />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="/join" element={<Join />} />
                    <Route path="/p/:id/:slug?" element={<PublicPin />} />
                    <Route path="/places/:town/:category?" element={<PublicPlaces />} />
                    <Route path="/s" element={<PublicSearch />} />
                    <Route path="/community-guidelines" element={<CommunityGuidelines />} />
                    <Route path="/claim/:listing_id" element={<ClaimListing />} />

                    {/* /dev folded into /admin?tab=dev */}
                    <Route path="/dev" element={<Navigate to="/admin?tab=dev" replace />} />

                    {/* Everything below requires a signed-in account */}
                    <Route path="/" element={Gated(<Home />)} />
                    <Route path="/listing/:id" element={Gated(<Listing />)} />
                    <Route path="/cart" element={Gated(<Cart />)} />
                    <Route path="/checkout/success" element={Gated(<CheckoutSuccess />)} />
                    <Route path="/checkout/cancel" element={Gated(<CheckoutCancel />)} />
                    <Route path="/dashboard" element={Gated(<Dashboard />)} />
                    <Route path="/dashboard/community" element={Gated(<Dashboard />)} />
                    <Route path="/dashboard/orders" element={Gated(<Dashboard />)} />
                    <Route path="/dashboard/photos" element={Gated(<DashboardPhotos />)} />
                    <Route path="/photographer/:user_id" element={Gated(<Photographer />)} />
                    <Route path="/feed" element={Gated(<Feed />)} />
                    <Route path="/saved" element={Gated(<Saved />)} />
                    <Route path="/admin" element={Gated(<Admin />)} />
                    <Route path="/gig-scanner" element={Gated(<GigScanner />)} />
                    <Route path="/dashboard/pin/:listing_id" element={Gated(<PinPage />)} />
                    <Route path="/dashboard/tickets" element={Gated(<Dashboard />)} />
                    <Route path="/dashboard/bookings" element={Gated(<Dashboard />)} />
                    <Route path="/my/bookings" element={Gated(<Dashboard />)} />
                    <Route path="/booking/success" element={Gated(<BookingSuccess />)} />
                    <Route path="/booking/cancel" element={Gated(<BookingCancel />)} />
                    <Route path="/dashboard/security" element={Gated(<Dashboard />)} />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </Suspense>
            </main>
            <Footer />
            <CookieBanner />
            <InstallPrompt />
        </div>
    );
}

export default function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <CartProvider>
                        <SavesProvider>
                            <AppRoutes />
                            <DevSwitcher />
                            <SnapBackPill />
                            <Toaster position="top-center" richColors />
                        </SavesProvider>
                    </CartProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}
